import React, { useEffect, useState } from "react";
import { IonModal } from "@ionic/react";
import { WebWarningContent } from "../ui/WebWarningModal";
import { Preferences } from "@capacitor/preferences";
import useAuth from "../../hooks/useAuth";
import { getUserDevices, ConnectedDevice } from "../../services/device";
import DeviceUsageConfirm from "../onboarding/SubComponents/DeviceUsageConfirm";
import QrScan from "../onboarding/SubComponents/QrScan";
import AddAnotherDevice from "../onboarding/SubComponents/AddAnotherDevice";
import { BiwazeEnum } from "../../utils/constants";
import { STEP_IDS } from "../onboarding/Onboarding";

// Step IDs for the device scan modal
export const DEVICE_SCAN_STEP_IDS = {
  DEVICE_USAGE_CONFIRM: "device-usage-confirm",
  QR_SCAN: "qr-scan",
  ADD_ANOTHER_DEVICE: "add-another-device",
} as const;

interface DeviceScanProps {
  isOpen: boolean;
  onClose: () => void;
  refresh?: () => void;
}

const DeviceScan: React.FC<DeviceScanProps> = ({
  isOpen,
  onClose,
  refresh,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const { user, isDesktop } = useAuth();

  // Form data state
  const [usesBiWazeDevice, setUsesBiWazeDevice] = useState<BiwazeEnum | "">("");
  const [connectedDevices, setConnectedDevices] = useState<ConnectedDevice[]>(
    []
  );

  useEffect(() => {
    if (isOpen) {
      fetchDevices();
      loadSavedStep();
    }
  }, [isOpen]);

  const fetchDevices = async () => {
    try {
      if (user?.profile_id) {
        const fetchedDevices = await getUserDevices(user.profile_id);
        console.log("Fetched devices:", fetchedDevices);
        setConnectedDevices(fetchedDevices);
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  const loadSavedStep = async () => {
    const { value } = await Preferences.get({ key: "deviceScanStep" });
    if (value) {
      const stepIndex = parseInt(value);
      console.log("Loaded saved step index:", stepIndex);
      setCurrentStep(stepIndex);
    }
  };

  const saveStep = async (step: number) => {
    await Preferences.set({
      key: "deviceScanStep",
      value: step.toString(),
    });
  };

  const closeModal = async () => {
    await Preferences.remove({ key: "deviceScanStep" });
    setCurrentStep(0);
    onClose();
    refresh && refresh();
  };

  const nextStep = (index?: number) => {
    // currentStep is always a number in this component
    const currentStepIndex = currentStep;
    if (index !== undefined && index >= 0 && index < steps.length) {
      console.log("Navigating to step:", index, steps[index]?.id);
      setCurrentStep(index);
      saveStep(index);
      return;
    }

    if (currentStepIndex < steps.length - 1) {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStep(nextStepIndex);
      saveStep(nextStepIndex);
    } else {
      // Reached the end, close modal
      closeModal();
    }
  };

  const prevStep = () => {
    const currentStepIndex = currentStep;
    if (currentStepIndex > 0) {
      const prevStepIndex = currentStepIndex - 1;
      setCurrentStep(prevStepIndex);
      saveStep(prevStepIndex);
    }
  };

  const handleSkipSetup = async () => {
    await Preferences.set({ key: "deviceScanSkipped", value: "true" });
    closeModal();
  };

  const handleComplete = async () => {
    console.log("Device scan completed");
    closeModal();
  };

  const handleNavigateToStep = (stepId: string) => {
    const stepIndex = steps.findIndex((step) => step.id === stepId);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
      saveStep(stepIndex);
    } else {
      console.warn("Step ID not found:", stepId);
      closeModal();
    }
  };

  // Define your steps here
  const steps = [
    // Step 0: Device usage confirmation
    {
      id: DEVICE_SCAN_STEP_IDS.DEVICE_USAGE_CONFIRM,
      render: () => (
        <DeviceUsageConfirm
          usesBiWazeDevice={usesBiWazeDevice}
          first_name={user?.first_name || ""}
          nextStep={() => handleNavigateToStep(STEP_IDS.QR_SCAN)}
          hideSkip={true}
          handleSkipSetup={handleSkipSetup}
          handleComplete={handleComplete}
          setUsesBiWazeDevice={setUsesBiWazeDevice}
          currentStep={currentStep}
          steps={steps}
          handleNavigateToStep={handleNavigateToStep}
          devices={connectedDevices.filter((device) => device.name)}
        />
      ),
    },
    // Step 1: QR code scanning instructions
    {
      id: DEVICE_SCAN_STEP_IDS.QR_SCAN,
      render: () => (
        <QrScan
          usesBiWazeDevice={usesBiWazeDevice}
          prevStep={prevStep}
          nextStep={nextStep}
          handleComplete={handleComplete}
          currentStep={currentStep}
          steps={steps}
          hideSkip={true}
          devices={connectedDevices}
        />
      ),
    },
    // Step 2: Add device with scanning functionality
    {
      id: DEVICE_SCAN_STEP_IDS.ADD_ANOTHER_DEVICE,
      render: () => (
        <AddAnotherDevice
          prevStep={prevStep}
          nextStep={nextStep}
          connectedDevices={connectedDevices}
          setConnectedDevices={setConnectedDevices}
          usesBiWazeDevice={usesBiWazeDevice}
          setUsesBiWazeDevice={setUsesBiWazeDevice}
          navigateToStep={handleNavigateToStep}
          buttonText="Done"
        />
      ),
    },
  ];

  return (
    <>
      <IonModal
        isOpen={isOpen}
        onDidDismiss={closeModal}
        className="device-scan-modal"
      >
        <div className="status-background"></div>
        <div style={{ width: "100%", height: "100%" }}>
          {isDesktop ? (
            <WebWarningContent
              title="Scan QR Code on Mobile"
              message="To link your BiWaze® device, open the Arc Connect app on your iOS or Android phone and use it to scan the QR code on your device."
              hideCloseButton
              onClose={closeModal}
            />
          ) : (
            steps[currentStep]?.render()
          )}
        </div>
      </IonModal>
    </>
  );
};

export default DeviceScan;
